import type {
  AssignedWorkflow,
  WorkflowAction,
} from '../../../shared/hooks/useIncidentWorkflow'
import {
  formatActionTimestamp,
  useWorkflowActionUserNames,
} from '../../../shared/hooks/useWorkflowActionUserNames'
import { useWorkflowInProgress } from '../../../shared/hooks/useWorkflowInProgress'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Box,
  QuestionCard,
  Spinner,
  SteppedList,
  SteppedListSection,
  Text,
  questionCardHelperTextStyles,
} from '@hivewatch/dark'

interface WorkflowInProgressStateIProps {
  assignedWorkflow: AssignedWorkflow | null
  currentStepId: string | null
  incidentId: string | null
  isActiveOwner: boolean
  workflowActions: WorkflowAction[]
}

export const WorkflowInProgressState = ({
  assignedWorkflow,
  currentStepId,
  incidentId,
  isActiveOwner,
  workflowActions,
}: WorkflowInProgressStateIProps) => {
  const mode = useLightMode()
  const { t } = useTranslation()
  const stepMap = new Map(assignedWorkflow?.steps.map((s) => [s.id, s]) ?? [])
  const currentStep = currentStepId ? stepMap.get(currentStepId) ?? null : null

  const {
    selectedIndex,
    isSubmitting,
    updateSelectedIndex,
    submitSelectedAnswer,
  } = useWorkflowInProgress({ currentStep, incidentId, isActiveOwner })

  const userNameMap = useWorkflowActionUserNames(workflowActions)

  const completedActions = workflowActions.filter(
    (a) => a.sourceNodeId !== currentStepId
  )

  return (
    <SteppedList colorScheme={mode}>
      {completedActions.map((action) => (
        <SteppedListSection
          colorScheme={mode}
          content={action.outcomeValue}
          header={
            stepMap.get(action.sourceNodeId)?.prompt ?? action.sourceNodeId
          }
          key={action.id}
          metadata={{
            completedAt: formatActionTimestamp(action.actionTimestamp),
            // Fall back to an empty name (not the raw userId) while the users
            // query is still resolving. Rendering the long userId UUID here
            // squeezes the section header into a narrow column and causes a
            // jarring reflow once the real name loads.
            name: userNameMap.get(action.userId) ?? '',
          }}
        />
      ))}
      <Text {...questionCardHelperTextStyles(mode)}>
        {isActiveOwner
          ? t('incidents:drawer_workflow_in_progress_select_response')
          : t('incidents:drawer_workflow_in_progress_not_owner')}
      </Text>
      <Box
        opacity={isSubmitting || !isActiveOwner ? 0.5 : 1}
        pointerEvents={isSubmitting || !isActiveOwner ? 'none' : 'auto'}
        position='relative'
      >
        {isSubmitting && (
          <Box
            alignItems='center'
            bottom={0}
            display='flex'
            justifyContent='center'
            left={0}
            position='absolute'
            right={0}
            top={0}
            zIndex={1}
          >
            <Spinner size='md' />
          </Box>
        )}
        <QuestionCard
          answerOptions={
            currentStep?.edges.map((e, i) => ({
              id: String(i),
              label: e.label,
            })) ?? []
          }
          colorScheme={mode}
          onSelectAnswer={(id) => {
            const idx = Number(id)
            if (!isNaN(idx)) {
              updateSelectedIndex(idx)
              submitSelectedAnswer('click')
            }
          }}
          question={currentStep?.prompt ?? ''}
          selectedAnswerId={String(selectedIndex)}
        />
      </Box>
    </SteppedList>
  )
}
